<script>
import { mapGetters } from 'vuex';
import { getContrastingTextColor } from '@chatwoot/utils';
import nextAvailabilityTime from 'widget/mixins/nextAvailabilityTime';
import configMixin from 'widget/mixins/configMixin';
import availabilityMixin from 'widget/mixins/availability';
import { IFrameHelper } from 'widget/helpers/utils';
import GroupedAvatars from 'widget/components/GroupedAvatars.vue';

import { GAPIFY_ON_START_CONVERSATION } from '../constants/sdkEvents';
export default {
  name: 'TeamAvailability',
  components: {
    GroupedAvatars,
  },
  mixins: [configMixin, nextAvailabilityTime, availabilityMixin],
  props: {
    availableAgents: {
      type: Array,
      default: () => {},
    },
    hasConversation: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['startConversation'],

  computed: {
    ...mapGetters({
      widgetColor: 'appConfig/getWidgetColor',
      availableMessage: 'appConfig/getAvailableMessage',
      unavailableMessage: 'appConfig/getUnavailableMessage',
    }),
    textColor() {
      return getContrastingTextColor(this.widgetColor);
    },
    agentAvatars() {
      return this.availableAgents.map(agent => ({
        name: agent.name,
        avatar: agent.avatar_url,
        id: agent.id,
      }));
    },
    headerMessage() {
      return this.isOnline
        ? this.availableMessage || this.$t('TEAM_AVAILABILITY.ONLINE')
        : this.unavailableMessage || this.$t('TEAM_AVAILABILITY.OFFLINE');
    },
    isOnline() {
      const { workingHoursEnabled } = this.channelConfig;
      const anyAgentOnline = this.availableAgents.length > 0;

      if (workingHoursEnabled) {
        return this.isInBetweenTheWorkingHours;
      }
      return anyAgentOnline;
    },
    isRtl() {
      const docDir = typeof document !== 'undefined' ? document?.dir : '';
      if (docDir) return docDir === 'rtl';
      const composer = this.$i18n;
      const currentLocale =
        composer?.locale?.value ??
        composer?.locale ??
        'en';
      const base = String(currentLocale).split('-')[0]; // fa-IR -> fa
      const RTL_LOCALES = ['ar', 'fa', 'he', 'ur', 'ps'];
      return RTL_LOCALES.includes(base);
    },
  },
  methods: {
    startConversation() {
      this.$emit('startConversation');
      if (!this.hasConversation) {
        IFrameHelper.sendMessage({
          event: 'onEvent',
          eventIdentifier: GAPIFY_ON_START_CONVERSATION,
          data: { hasConversation: false },
        });
      }
    },
  },
};
</script>

<template>
  <div
    class="flex flex-col gap-3 w-full shadow outline-1 outline outline-n-container rounded-xl bg-n-background dark:bg-n-solid-2 px-5 py-4"
  >
    <div class="flex items-center justify-between gap-2">
      <div class="flex flex-col gap-1">
        <div class="font-medium text-n-slate-12 line-clamp-2">
          {{ headerMessage }}
        </div>
        <div class="text-n-slate-11">
          {{ replyWaitMessage }}
        </div>
      </div>
      <GroupedAvatars v-if="isOnline" :users="availableAgents" />
    </div>
    <button
      class="inline-flex items-center gap-1 font-medium text-n-slate-12"
      :style="{ color: widgetColor }"
      @click="startConversation"
    >
      <span>
        {{
          hasConversation
            ? $t('CONTINUE_CONVERSATION')
            : $t('START_CONVERSATION')
        }}
      </span>
      <i :class="[isRtl ? 'i-lucide-chevron-left' : 'i-lucide-chevron-right', 'size-5', 'mt-px']" />

    </button>
  </div>
</template>
